import {
  consultantOptionsSchema,
  PROJECT_PHASE_LABELS,
  PROJECT_PHASES,
  projectCreateInputSchema,
  projectInputSchema,
  projectOptionsSchema,
  projectTemplateListSchema,
  type Project,
  type ProjectCreateInput,
  type ProjectPhaseName,
} from '@fluxus/shared';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { plural } from '@/features/templates/plural';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

type Props = {
  initial?: Project;
  /** Abrir projeto na empresa: com ela, o formulário oferece partir de um modelo (T-204b; DEC-070). */
  organizationId?: string;
  submitLabel: string;
  onSubmit: (input: ProjectCreateInput) => Promise<unknown>;
  onCancel: () => void;
  onBusyChange?: (busy: boolean) => void;
};

/** Nova tentativa de uma lista do formulário que não carregou. */
function RetryButton({ onClick, busy }: { onClick: () => void; busy: boolean }) {
  return (
    <Button type="button" variant="outline" size="sm" onClick={onClick} loading={busy}>
      {busy ? null : <RefreshCw aria-hidden />}
      Tentar novamente
    </Button>
  );
}

/** Erros dos campos do modelo vindos da API (`template.kpiDepartmentId` → `kpiDepartmentId`). */
function templateFieldErrors(err: unknown): FieldErrors {
  if (!(err instanceof ApiError)) return {};
  const out: FieldErrors = {};
  for (const d of err.details) {
    const field = /^template\.(\w+)$/.exec(d.path)?.[1];
    if (field && !out[field]) out[field] = d.message;
  }
  return out;
}

/**
 * Projeto BSC: nome, horizonte estratégico, fase e consultor responsável (equipe Fluxus). Ao abrir, pode partir de um
 * modelo de projeto; se o modelo tem KPIs, pede a área e o responsável que eles recebem.
 */
export function ProjectForm({
  initial,
  organizationId,
  submitLabel,
  onSubmit,
  onCancel,
  onBusyChange,
}: Props) {
  const { client } = useAuth();
  const year = new Date().getFullYear();
  const [name, setName] = useState(initial?.name ?? '');
  const [horizonStart, setHorizonStart] = useState(String(initial?.horizonStart ?? year + 1));
  const [horizonEnd, setHorizonEnd] = useState(String(initial?.horizonEnd ?? year + 5));
  const [phase, setPhase] = useState<ProjectPhaseName>(initial?.phase ?? 'DIAGNOSIS');
  const [consultantId, setConsultantId] = useState(initial?.consultant?.id ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [kpiDepartmentId, setKpiDepartmentId] = useState('');
  const [kpiOwnerId, setKpiOwnerId] = useState('');
  const ids = {
    name: useId(),
    start: useId(),
    end: useId(),
    phase: useId(),
    consultant: useId(),
    template: useId(),
    department: useId(),
    owner: useId(),
  };
  const creating = !initial && Boolean(organizationId);
  const templates = useQuery({
    queryKey: ['project-templates'],
    queryFn: ({ signal }) =>
      client.request('/project-templates', { schema: projectTemplateListSchema, signal }),
    enabled: creating,
  });
  const template = templates.data?.items.find((t) => t.id === templateId) ?? null;
  const needsKpiDefaults = Boolean(template && template.counts.kpis > 0);
  const options = useQuery({
    queryKey: ['project-options', organizationId],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/project-options`, {
        schema: projectOptionsSchema,
        signal,
      }),
    enabled: creating && needsKpiDefaults,
  });

  const consultants = useQuery({
    queryKey: ['consultants'],
    queryFn: ({ signal }) => client.request('/consultants', { schema: consultantOptionsSchema, signal }),
  });

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const input = {
      name,
      horizonStart,
      horizonEnd,
      phase,
      consultantId: consultantId || null,
      ...(creating && templateId
        ? {
            template: {
              templateId,
              kpiDepartmentId: needsKpiDefaults ? kpiDepartmentId || null : null,
              kpiOwnerId: needsKpiDefaults ? kpiOwnerId || null : null,
            },
          }
        : {}),
    };
    const parsed = (creating ? projectCreateInputSchema : projectInputSchema).safeParse(input);
    // Modelo com KPIs: a área e o responsável que eles recebem (mesma regra da API).
    const missing: FieldErrors = needsKpiDefaults
      ? {
          kpiDepartmentId: kpiDepartmentId ? undefined : 'Escolha a área dos indicadores do modelo.',
          kpiOwnerId: kpiOwnerId ? undefined : 'Escolha o responsável pelos indicadores do modelo.',
        }
      : {};
    if (!parsed.success || hasFieldErrors(missing)) {
      setErrors({ ...(parsed.success ? {} : zodFieldErrors(parsed.error)), ...missing });
      return;
    }
    setErrors({});
    setSaving(true);
    onBusyChange?.(true);
    try {
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = { ...apiFieldErrors(err), ...templateFieldErrors(err), template: undefined };
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange?.(false);
    }
  }

  const clear = (field: string) => setErrors((e) => ({ ...e, [field]: undefined }));
  // Lista que não carregou: a mensagem fica ligada ao campo (aria-invalid e aria-describedby), como no consultor.
  const templateError = errors.templateId ?? (templates.isError ? errorMessage(templates.error) : undefined);
  const optionsError = options.isError ? errorMessage(options.error) : undefined;
  const departmentError = errors.kpiDepartmentId ?? optionsError;
  const ownerError = errors.kpiOwnerId ?? optionsError;
  const consultantError =
    errors.consultantId ?? (consultants.isError ? errorMessage(consultants.error) : undefined);
  const currentOutsideTeam =
    !!initial?.consultant &&
    !!consultants.data &&
    !consultants.data.items.some((c) => c.id === initial.consultant?.id);

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.name} label="Nome do projeto" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={120}
          placeholder="Ex.: BSC 2027–2031"
          onChange={(e) => {
            setName(e.target.value);
            clear('name');
          }}
          className="h-11"
        />
      </FormField>
      <fieldset className="grid grid-cols-2 gap-3">
        <legend className="mb-1.5 text-sm font-medium">Horizonte estratégico</legend>
        <FormField id={ids.start} label="Início (ano)" error={errors.horizonStart}>
          <Input
            {...fieldA11y(ids.start, errors.horizonStart)}
            value={horizonStart}
            inputMode="numeric"
            maxLength={4}
            onChange={(e) => {
              setHorizonStart(e.target.value.replace(/\D/g, ''));
              clear('horizonStart');
            }}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.end} label="Fim (ano)" error={errors.horizonEnd}>
          <Input
            {...fieldA11y(ids.end, errors.horizonEnd)}
            value={horizonEnd}
            inputMode="numeric"
            maxLength={4}
            onChange={(e) => {
              setHorizonEnd(e.target.value.replace(/\D/g, ''));
              clear('horizonEnd');
            }}
            className="h-11"
          />
        </FormField>
      </fieldset>
      <FormField id={ids.phase} label="Fase" error={errors.phase}>
        <NativeSelect
          {...fieldA11y(ids.phase, errors.phase)}
          value={phase}
          onChange={(e) => setPhase(e.target.value as ProjectPhaseName)}
        >
          {PROJECT_PHASES.map((p) => (
            <option key={p} value={p}>
              {PROJECT_PHASE_LABELS[p]}
            </option>
          ))}
        </NativeSelect>
      </FormField>
      <FormField
        id={ids.consultant}
        label="Consultor responsável"
        error={consultantError}
        action={
          consultants.isError ? (
            <RetryButton onClick={() => void consultants.refetch()} busy={consultants.isFetching} />
          ) : undefined
        }
      >
        <NativeSelect
          {...fieldA11y(ids.consultant, consultantError)}
          value={consultantId}
          disabled={consultants.isPending}
          onChange={(e) => {
            setConsultantId(e.target.value);
            clear('consultantId');
          }}
        >
          <option value="">
            {consultants.isPending ? 'Carregando consultores…' : 'Sem consultor definido'}
          </option>
          {/* Consultor atual que saiu da equipe ativa continua como opção (salvar não o troca). */}
          {currentOutsideTeam ? (
            <option value={initial!.consultant!.id}>
              {initial!.consultant!.name} (fora da equipe ativa)
            </option>
          ) : null}
          {consultants.data?.items.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </NativeSelect>
      </FormField>
      {creating ? (
        <fieldset className="flex flex-col gap-3 rounded-lg border border-border p-4">
          <legend className="px-1 text-sm font-medium">Ponto de partida</legend>
          <FormField
            id={ids.template}
            label="Modelo de projeto"
            error={templateError}
            action={
              templates.isError ? (
                <RetryButton onClick={() => void templates.refetch()} busy={templates.isFetching} />
              ) : undefined
            }
            hint={
              template
                ? `Traz ${plural(template.counts.perspectives, 'perspectiva', 'perspectivas')}, ${plural(template.counts.objectives, 'objetivo', 'objetivos')} e ${plural(template.counts.kpis, 'KPI', 'KPIs')}, sem metas nem resultados.`
                : templates.data && templates.data.items.length === 0
                  ? 'Nenhum modelo cadastrado: o projeto começa com as quatro perspectivas padrão.'
                  : 'Em branco, o projeto começa com as quatro perspectivas padrão do BSC.'
            }
          >
            <NativeSelect
              {...fieldA11y(ids.template, templateError, true)}
              value={templateId}
              disabled={templates.isPending || templates.isError || saving}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setErrors((x) => ({
                  ...x,
                  templateId: undefined,
                  kpiDepartmentId: undefined,
                  kpiOwnerId: undefined,
                }));
              }}
            >
              <option value="">{templates.isPending ? 'Carregando modelos…' : 'Projeto em branco'}</option>
              {templates.data?.items.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          {needsKpiDefaults ? (
            <>
              <p className="text-sm text-muted-foreground">
                Os KPIs do modelo recebem esta área e este responsável; dá para ajustar cada um depois.
              </p>
              <FormField
                id={ids.department}
                label="Área dos indicadores"
                error={departmentError}
                action={
                  options.isError ? (
                    <RetryButton onClick={() => void options.refetch()} busy={options.isFetching} />
                  ) : undefined
                }
              >
                <NativeSelect
                  {...fieldA11y(ids.department, departmentError)}
                  value={kpiDepartmentId}
                  disabled={options.isPending || options.isError || saving}
                  onChange={(e) => {
                    setKpiDepartmentId(e.target.value);
                    clear('kpiDepartmentId');
                  }}
                >
                  <option value="">{options.isPending ? 'Carregando áreas…' : 'Escolha a área'}</option>
                  {options.data?.departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </NativeSelect>
              </FormField>
              <FormField id={ids.owner} label="Responsável pelos indicadores" error={ownerError}>
                <NativeSelect
                  {...fieldA11y(ids.owner, ownerError)}
                  value={kpiOwnerId}
                  disabled={options.isPending || options.isError || saving}
                  onChange={(e) => {
                    setKpiOwnerId(e.target.value);
                    clear('kpiOwnerId');
                  }}
                >
                  <option value="">
                    {options.isPending ? 'Carregando pessoas…' : 'Escolha o responsável'}
                  </option>
                  {options.data?.people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </NativeSelect>
              </FormField>
              {options.data && (!options.data.departments.length || !options.data.people.length) ? (
                <FormAlert tone="error">
                  A empresa precisa de pelo menos uma área ativa e uma pessoa ativa para receber os KPIs do
                  modelo. Cadastre-as em Áreas e Usuários da empresa ou abra o projeto em branco.
                </FormAlert>
              ) : null}
            </>
          ) : null}
        </fieldset>
      ) : null}
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
